// client/src/components/character_card/CharacterAdminActions.jsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  LocalOffer as EmancipateIcon,
  SwapHoriz as ChangeOwnerIcon,
} from '@mui/icons-material';
import Gateway from '../../api/gateway';

/**
 * Admin-действия на карточке персонажа:
 *  - «Освободить» (эмансипация)
 *  - «Сменить владельца»
 *
 * Показывать только если `isAdmin` (system_role в admin/superadmin).
 *
 * @param {object} props
 * @param {object} props.character — { character_id, status, ... }
 * @param {boolean} [props.isOwn] — true, если это свой персонаж
 * @param {function} [props.onAfterChange] — колбэк после успеха (обновить списки)
 */
export default function CharacterAdminActions({ character, isOwn = false, onAfterChange }) {
  const [emancipating, setEmancipating] = useState(false);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);

  if (!character?.character_id) return null;

  const characterId = character.character_id;
  const canEmancipate = character.status !== 'emancipated' && character.status !== 'dead' && character.status !== 'archived';

  const handleEmancipate = async () => {
    if (!window.confirm(
      `Освободить персонажа "${character.name || characterId}"? Владелец больше не сможет им управлять.`
    )) return;

    setEmancipating(true);
    try {
      await Gateway.http.imcon.post('/api/access/emancipate', {
        character_id: characterId,
      });
      if (onAfterChange) await onAfterChange();
    } catch (err) {
      console.error('❌ emancipate:', err);
      alert('Не удалось освободить: ' + (err.response?.data?.error || err.message));
    } finally {
      setEmancipating(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
      {canEmancipate && (
        <Tooltip title="Освободить персонажа (эмансипация)">
          <span>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={emancipating ? <CircularProgress size={14} /> : <EmancipateIcon />}
              onClick={handleEmancipate}
              disabled={emancipating}
              sx={{ fontSize: '0.7rem' }}
            >
              Освободить
            </Button>
          </span>
        </Tooltip>
      )}

      <Tooltip title="Передать персонажа другому игроку">
        <Button
          size="small"
          variant="outlined"
          color="info"
          startIcon={<ChangeOwnerIcon />}
          onClick={() => setOwnerDialogOpen(true)}
          sx={{ fontSize: '0.7rem' }}
        >
          Сменить владельца
        </Button>
      </Tooltip>

      <ChangeOwnerDialog
        open={ownerDialogOpen}
        onClose={() => setOwnerDialogOpen(false)}
        character={character}
        onSuccess={onAfterChange}
      />
    </Box>
  );
}

// ============================================================================
// Диалог смены владельца
// ============================================================================

function ChangeOwnerDialog({ open, onClose, character, onSuccess }) {
  const [players, setPlayers] = React.useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError('');
    setSelectedPlayerId('');

    Gateway.api.accessAdmin
      .listUsers()
      .then((users) => {
        setPlayers(users || []);
      })
      .catch((err) => {
        setError('Не удалось загрузить список игроков: ' + err.message);
      })
      .finally(() => setLoading(false));
  }, [open]);

  const handleConfirm = async () => {
    if (!selectedPlayerId) {
      setError('Выберите нового владельца');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await Gateway.http.imcon.patch(
        `/api/access/characters/${character.character_id}/owner`,
        { new_owner_player_id: selectedPlayerId }
      );
      if (onSuccess) await onSuccess();
      onClose();
    } catch (err) {
      console.error('❌ change owner:', err);
      setError(err.response?.data?.error || err.message || 'Не удалось сменить владельца');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Сменить владельца: {character.name || character.character_id}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Новый владелец</InputLabel>
            <Select
              value={selectedPlayerId}
              onChange={(e) => setSelectedPlayerId(e.target.value)}
              label="Новый владелец"
            >
              {players.map((p) => (
                <MenuItem key={p.kratos_identity_id} value={p.player_id}>
                  {p.display_name || p.email} ({p.system_role})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Отмена</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={saving || !selectedPlayerId}
          startIcon={saving ? <CircularProgress size={14} /> : <ChangeOwnerIcon />}
        >
          {saving ? 'Сохранение...' : 'Сменить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
